import '@/lib/dropdown'
import 'semantic-ui-api/api'

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    config: { type: Object, default: {} }
  }

  connect() {
    this.$element = $(this.element)

    if (!this.element.classList.contains('selection')) {
      this.element.classList.add('dropdown--from-button')
    }

    this.$element.dropdown({
      ...this.config(),
      onShow: () => setTimeout(() => this.adjustDirection(), 0)
    })

    // Fomantic replaces a <select> by a wrapper div carrying its classes.
    this.$dropdown = this.$element.closest('.dropdown')

    if (this.$dropdown.hasClass('allow-additions')) {
      if (this.isMultiple()) {
        this.setupMultipleAllowAdditions()
      } else {
        this.setupComboSelect()
      }
    }
  }

  disconnect() {
    this.$search?.off('.dropdownController')
    this.$dropdown?.off('.dropdownController')
  }

  // Multiple `allow-additions` must not use Fomantic 2.9 forceSelection: on blur
  // it clicks the highlighted menu item, which adds a contact by accident.
  // Commit only an explicit non-empty search term (old Fomantic behavior).
  setupMultipleAllowAdditions() {
    this.$search = this.$dropdown.find('input.search')

    // required <select> and config force forceSelection back on during init.
    this.$dropdown.dropdown('setting', 'forceSelection', false)

    this.$search.on('keydown.dropdownController', event => {
      if (event.keyCode === 13) event.preventDefault()
    })
    this.$dropdown.on('mousedown.dropdownController', '.menu', () => {
      this.skipBlurCommit = true
    })
    this.$search.on('blur.dropdownController', () => {
      if (this.skipBlurCommit) {
        this.skipBlurCommit = false
        return
      }

      const value = this.$search.val().trim()
      if (!value) return

      this.$dropdown.dropdown('set selected', value)
    })
  }

  // Single `allow-additions` dropdowns behave as a combobox: the search input
  // always shows the committed value, and whatever it contains on blur or
  // Enter becomes the selection (an emptied input clears it).
  setupComboSelect() {
    this.$search = this.$dropdown.find('input.search')
    this.$text = this.$dropdown.find('> .text')

    // A required <select> turns forceSelection back on during init; the
    // combobox commits the typed text itself, so keep Fomantic out of blur.
    this.$dropdown.dropdown('setting', 'forceSelection', false)
    // Keep Fomantic from wiping the search input after an item is selected.
    this.$dropdown.dropdown('setting', 'keepSearchTerm', true)

    // Delegate on the hidden select only: the search input fires its own
    // native change on blur, which must not resync the old selection.
    this.$dropdown.on('change.dropdownController', 'select', () => this.syncSearchInput())
    this.syncSearchInput()

    this.$search.on('keydown.dropdownController', event => {
      if (event.keyCode !== 13) return

      event.preventDefault()
      event.stopImmediatePropagation()
      this.commitSearchValue()
      this.$dropdown.dropdown('hide')
    })
    this.$search.on('click.dropdownController', () => this.$search[0].select())
    // Only user edits fire `input`: Fomantic empties the search input
    // programmatically after a selection, which must not clear it.
    this.$search.on('input.dropdownController', () => {
      this.searchEdited = true
      if (!this.$search.val()) this.commitSearchValue()
    })
    // A menu click blurs the search input first: let Fomantic handle it alone.
    this.$dropdown.on('mousedown.dropdownController', '.menu', () => {
      this.skipBlurCommit = true
    })
    this.$search.on('focus.dropdownController', () => {
      this.skipBlurCommit = false
      this.searchEdited = false
    })
    this.$search.on('blur.dropdownController', () => {
      if (this.skipBlurCommit) {
        this.skipBlurCommit = false
      } else if (this.$search.val() || this.searchEdited) {
        this.commitSearchValue()
      } else {
        this.syncSearchInput()
      }
    })
  }

  commitSearchValue() {
    const value = this.$search.val().trim()

    if (value !== this.selectedValue()) {
      if (value) {
        this.$dropdown.dropdown('set selected', value)
      } else {
        this.$dropdown.dropdown('clear')
      }
    }

    this.syncSearchInput()
  }

  syncSearchInput() {
    const value = this.selectedValue()
    this.$search.val(value)
    this.$text.toggleClass('filtered', !!value)
  }

  isMultiple() {
    return this.$element.is('[multiple]') || this.$element.hasClass('multiple')
  }

  selectedValue() {
    return this.$dropdown.find('select').val() || ''
  }

  adjustDirection() {
    if ('dropdownNoUpward' in this.$element[0].dataset) return
    const rect = this.element.getBoundingClientRect()
    const menu = this.$element.find('.menu')

    if (menu.length === 0) return

    const menuHeight = menu.outerHeight()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top

    const isInModal = this.element.closest('[data-controller~="ui--modal-component"]')

    if ((spaceBelow < menuHeight && spaceAbove > menuHeight) || isInModal) {
      this.$element.addClass('upward')
    } else {
      this.$element.removeClass('upward')
    }
  }

  config() {
    const isApi = !!this.configValue.apiSettings

    const baseConfig = {
      direction: 'auto',
      forceSelection: false,
      ignoreDiacritics: true,
      ignoreSearchCase: true,
      preserveHTML: false,
      match: 'text',
      className: {
        dropdown: 'dropdown',
        label: 'label label-default'
      },
      selector: {
        dropdown: '.dropdown'
      },
      onChange(value, text, $choice) {
        if ($(this).hasClass('search')) document.activeElement.blur()
        // Select-based dropdowns get a native change from Fomantic itself;
        // dispatching another one here re-enters Fomantic's own change
        // handler, which resets the selection and wipes the search input.
        if (this.tagName !== 'SELECT') this.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }

    if (!isApi) {
      baseConfig.fullTextSearch = 'exact'
    }

    const o = {
      ...baseConfig,
      ...(this.$element.hasClass('dropdown-with-blank') ? { placeholder: false } : {}),
      ...(this.$element.hasClass('allow-additions') ? { allowAdditions: true, hideAdditions: !this.isMultiple() } : {}),
      ...this.configValue
    }

    if (isApi && o.apiSettings) {
      o.apiSettings = {
        ...o.apiSettings,
        throttleFirstRequest: false,
        cache: false,
        responseAsync: (settings, resolve) => {
          $.ajax({
            url: settings.url,
            dataType: 'json',
            success: response => resolve(response),
            error: () => resolve({ success: false })
          })
        }
      }
    }

    return o
  }
}
